<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import Swal from "sweetalert2";
import axios from "axios";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import { formatDateToWeb } from "@/helpers/date-functions.js";

import { usePermissions } from "@/composables/usePermissions";

export default {
    page: {
        title: "Create Alternative Device Request",
        meta: [{ name: "description", content: appConfig.description }],
    },

    components: {
        Layout,
        PageHeader,
        Multiselect,
    },

    setup() {
        const permissions = usePermissions();
        return {
            permissions,
        };
    },

    data() {
        return {
            title: "Create Alternative Device Request",
            items: [
                {
                    text: "Device Management",
                    href: "/",
                },
                {
                    text: "Alternative Devices",
                    href: "/device/alternative-devices",
                },
                {
                    text: "Create Request",
                    active: true,
                },
            ],
            selectedOrderObject: null,
            selectedOrderId: "",
            orderData: null,

            // Form fields
            requestType: "",
            selectedDeviceId: "",
            selectedAlternativeDevice: null,
            availableAlternativeDevices: [],
            remark: "",

            // Loading states
            loadingOrders: false,
            loadingOrderDetails: false,
            loadingAlternatives: false,
            submitting: false,
        };
    },

    mounted() {
        this.loadInitialUrlOrder();
    },

    watch: {
        selectedOrderObject(newValue, oldValue) {
            console.log("selectedOrderObject changed:", { newValue, oldValue });
            if (!newValue && oldValue) {
                this.onOrderClear();
            }
        },
    },

    computed: {
        alternativeDeviceOptions() {
            return this.availableAlternativeDevices.map((device) => ({
                value: device.id,
                label: this.getDeviceDisplayName(device),
                device: device,
            }));
        },
    },

    methods: {
        setAuthHeader() {
            const token = localStorage.getItem("jwt");
            if (token) {
                axios.defaults.headers.common["authorization"] = "Bearer " + token;
            }
        },

        async fetchOrdersDropdown(query) {
            console.log("Fetching orders dropdown with query:", query);
            if (!query) {
                // Fetch initial 50 latest orders if no query typed
                try {
                    this.setAuthHeader();
                    const res = await axios.get(process.env.VUE_APP_API_BASEURL + "request-forms/orders-dropdown");
                    const data = res.data?.data || res.data || [];
                    return data.map((ord) => {
                        const customerName = ord.customer && ord.customer.name_as_per_nric ? ` - ${ord.customer.name_as_per_nric}` : '';
                        return {
                            value: ord.id,
                            label: `${ord.orderId}${customerName}`,
                        };
                    });
                } catch (error) {
                    console.error("Failed to fetch initial orders:", error);
                    return [];
                }
            }

            try {
                this.setAuthHeader();
                const url = process.env.VUE_APP_API_BASEURL + "request-forms/orders-dropdown?search=" + encodeURIComponent(query);
                const res = await axios.get(url);
                const data = res.data?.data || res.data || [];
                return data.map((ord) => {
                    const customerName = ord.customer && ord.customer.name_as_per_nric ? ` - ${ord.customer.name_as_per_nric}` : '';
                    return {
                        value: ord.id,
                        label: `${ord.orderId}${customerName}`,
                    };
                });
            } catch (error) {
                console.error("Failed to search orders:", error);
                return [];
            }
        },

        async loadInitialUrlOrder() {
            const urlOrderId = this.$route.query.order_id;
            if (!urlOrderId) return;
            try {
                this.setAuthHeader();
                const res = await axios.get(process.env.VUE_APP_API_BASEURL + `request-forms/orders-dropdown?search=${encodeURIComponent(urlOrderId)}`);
                const data = res.data?.data || res.data || [];
                let matched = null;
                if (data.length > 0) {
                    matched = data.find(
                        (o) => o.id == urlOrderId || (o.orderId && o.orderId.toLowerCase() === urlOrderId.toLowerCase())
                    ) || data[0];
                }
                if (matched) {
                    const customerName = matched.customer && matched.customer.name_as_per_nric ? ` - ${matched.customer.name_as_per_nric}` : '';
                    this.selectedOrderObject = {
                        value: matched.id,
                        label: `${matched.orderId}${customerName}`,
                    };
                    this.onOrderSelect(this.selectedOrderObject);
                } else {
                    // Fallback to fetch order details directly if search didn't return dropdown item
                    const detailsRes = await axios.get(process.env.VUE_APP_API_BASEURL + `request-forms/order-details/${urlOrderId}`);
                    if (detailsRes.data && detailsRes.data.success && detailsRes.data.data?.order) {
                        const ord = detailsRes.data.data.order;
                        const customerName = ord.customer && ord.customer.name_as_per_nric ? ` - ${ord.customer.name_as_per_nric}` : '';
                        this.selectedOrderObject = {
                            value: ord.id,
                            label: `${ord.orderId}${customerName}`,
                        };
                        this.onOrderSelect(this.selectedOrderObject);
                    } else {
                        Swal.fire({
                            icon: "warning",
                            title: "Order Not Found or Ineligible",
                            text: `Order "${urlOrderId}" is either invalid or not under 'Processing' status. Only Processing orders can apply for alternative devices.`,
                        });
                    }
                }
            } catch (e) {
                console.error("Failed to resolve URL order ID:", e);
                Swal.fire({
                    icon: "warning",
                    title: "Order Not Found or Ineligible",
                    text: `Order "${urlOrderId}" is either invalid or not under 'Processing' status.`,
                });
            }
        },

        onOrderClear() {
            console.log("Order selection cleared.");
            this.selectedOrderId = "";
            this.selectedOrderObject = null;
            this.orderData = null;
            this.requestType = "";
            this.selectedDeviceId = "";
            this.selectedAlternativeDevice = null;
            this.availableAlternativeDevices = [];
            this.remark = "";
            if (this.$route.query.order_id) {
                this.$router.replace({ query: {} }).catch(() => {});
            }
        },

        async onOrderSelect(selectedOption) {
            
            // Reset downstream form fields when order selection changes
            this.requestType = "";
            this.selectedDeviceId = "";
            this.selectedAlternativeDevice = null;
            this.availableAlternativeDevices = [];
            this.remark = "";

            const orderId = selectedOption?.value || selectedOption;
            this.selectedOrderId = orderId || "";

            if (!this.selectedOrderId) {
                this.orderData = null;
                if (this.$route.query.order_id) {
                    this.$router.replace({ query: {} }).catch(() => {});
                }
                return;
            }

            // Append order_id to URL params without reloading page
            if (this.$route.query.order_id !== String(this.selectedOrderId)) {
                this.$router.replace({ query: { ...this.$route.query, order_id: this.selectedOrderId } }).catch(() => {});
            }

            this.loadingOrderDetails = true;
            try {
                this.setAuthHeader();
                const res = await axios.get(process.env.VUE_APP_API_BASEURL + `request-forms/order-details/${this.selectedOrderId}`);
                if (res.data && res.data.success) {
                    this.orderData = res.data.data;
                } else {
                    this.orderData = null;
                }
            } catch (error) {
                console.error("Failed to fetch order details:", error);
                this.orderData = null;
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to load order details.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
            } finally {
                this.loadingOrderDetails = false;
            }
        },

        async onRequestTypeChange() {
            this.selectedDeviceId = "";
            this.selectedAlternativeDevice = null;
            this.availableAlternativeDevices = [];

            if (!this.selectedOrderId || !this.requestType) {
                return;
            }

            this.loadingAlternatives = true;
            try {
                this.setAuthHeader();
                const res = await axios.post(process.env.VUE_APP_API_BASEURL + "request-forms/alternative-devices", {
                    order_id: this.selectedOrderId,
                    request_type: this.requestType,
                });

                if (res.data && res.data.success) {
                    const responseData = res.data.data;
                    if (responseData && Array.isArray(responseData.data)) {
                        this.availableAlternativeDevices = responseData.data;
                    } else if (Array.isArray(responseData)) {
                        this.availableAlternativeDevices = responseData;
                    } else {
                        this.availableAlternativeDevices = [];
                    }
                }
            } catch (error) {
                console.error("Failed to fetch alternative devices:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to load alternative devices.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
            } finally {
                this.loadingAlternatives = false;
            }
        },

        onDeviceSelect(val) {
            const id = typeof val === 'object' && val !== null ? val.value : val;
            this.selectedDeviceId = id || "";
            if (!this.selectedDeviceId) {
                this.selectedAlternativeDevice = null;
                return;
            }
            this.selectedAlternativeDevice = this.availableAlternativeDevices.find(
                (item) => item.id == this.selectedDeviceId
            ) || null;
        },

        onDeviceClear() {
            this.selectedDeviceId = "";
            this.selectedAlternativeDevice = null;
        },

        getDeviceVariant(device) {
            if (!device) return "";
            const parts = [];
            if (device.product?.color?.color_name) parts.push(device.product.color.color_name);
            if (device.product?.capacity?.capacity_name) parts.push(device.product.capacity.capacity_name);
            return parts.join(", ");
        },

        getDeviceDisplayName(device) {
            if (!device) return "";
            const name = device.product?.product_name || "";
            const variant = this.getDeviceVariant(device);
            if (name && variant) return `${name} (${variant})`;
            return name || variant || "";
        },

        formatCurrency(amount) {
            if (!amount && amount !== 0) return "RM 0.00";
            const num = parseFloat(amount);
            return isNaN(num) ? "RM 0.00" : `RM ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        formatDate(dateStr) {
            if (!dateStr) return "N/A";
            return formatDateToWeb(dateStr);
        },

        getPriceDiff(altDevice) {
            if (!altDevice || !this.orderData?.order?.monthly_subscription) return null;
            const originalPrice = parseFloat(this.orderData.order.monthly_subscription) || 0;
            const newPrice = parseFloat(altDevice.price) || 0;
            const diff = newPrice - originalPrice;
            if (Math.abs(diff) < 0.01) {
                return { type: 'same', icon: 'ri-equal-line', textClass: 'text-muted' };
            } else if (diff > 0) {
                return { type: 'higher', icon: 'ri-arrow-up-line', textClass: 'text-danger' };
            } else {
                return { type: 'lower', icon: 'ri-arrow-down-line', textClass: 'text-success' };
            }
        },

        getCostDiff(altDevice) {
            if (!altDevice || !this.orderData?.current_device?.device_cost) return null;
            const originalCost = parseFloat(this.orderData.current_device.device_cost) || 0;
            const newCost = parseFloat(altDevice.device_cost) || 0;
            const diff = newCost - originalCost;
            if (Math.abs(diff) < 0.01) {
                return { type: 'same', icon: 'ri-equal-line', textClass: 'text-muted' };
            } else if (diff > 0) {
                return { type: 'higher', icon: 'ri-arrow-up-line', textClass: 'text-danger' };
            } else {
                return { type: 'lower', icon: 'ri-arrow-down-line', textClass: 'text-success' };
            }
        },

        getCreditBadgeClass(status) {
            switch (status) {
                case "N1":
                    return "bg-success-subtle text-success border border-success-subtle";
                case "N2":
                    return "bg-warning-subtle text-warning border border-warning-subtle";
                case "N3":
                    return "bg-danger-subtle text-danger border border-danger-subtle";
                default:
                    return "bg-secondary-subtle text-secondary";
            }
        },

        async submitRequest() {
            if (!this.permissions.canRequestAlternativeDevices?.value) {
                Swal.fire({
                    icon: "error",
                    title: "Access Denied",
                    text: "You do not have permission to submit alternative device requests.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
                return;
            }

            if (!this.selectedOrderId) {
                Swal.fire({
                    icon: "warning",
                    title: "Missing Field",
                    text: "Please select an Order ID.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
                return;
            }

            if (!this.requestType) {
                Swal.fire({
                    icon: "warning",
                    title: "Missing Field",
                    text: "Please select a Request Type.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
                return;
            }

            if (!this.selectedDeviceId) {
                Swal.fire({
                    icon: "warning",
                    title: "Missing Field",
                    text: "Please select an Alternative Device.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
                return;
            }

            if (!this.remark || !this.remark.trim()) {
                Swal.fire({
                    icon: "warning",
                    title: "Missing Field",
                    text: "Please enter a remark / reason for the request.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
                return;
            }

            const currentDeviceId = this.orderData?.current_device?.id;
            if (!currentDeviceId) {
                Swal.fire({
                    icon: "error",
                    title: "Invalid State",
                    text: "Current device for this order could not be identified.",
                    customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                    buttonsStyling: false,
                });
                return;
            }

            const orderDisplayCode = this.orderData?.order?.orderId || this.selectedOrderId;
            const confirmMsg = this.requestType === "Color Change"
                ? `Request for Order ${orderDisplayCode} will be submitted directly for customer approval (No BU approval required).`
                : `Request for Order ${orderDisplayCode} will be submitted for BU approval.`;

            Swal.fire({
                title: "Submit Alternative Device Request?",
                text: confirmMsg,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Submit Request",
                cancelButtonText: "Cancel",
                customClass: {
                    confirmButton: "btn btn-primary w-xs me-2",
                    cancelButton: "btn btn-soft-secondary w-xs",
                },
                buttonsStyling: false,
            }).then(async (result) => {
                if (result.isConfirmed) {
                    this.submitting = true;
                    try {
                        this.setAuthHeader();
                        const payload = {
                            order_id: this.selectedOrderId,
                            request_type: this.requestType,
                            current_device_id: currentDeviceId,
                            new_device_id: this.selectedDeviceId,
                            remarks: this.remark || null,
                        };

                        const res = await axios.post(process.env.VUE_APP_API_BASEURL + "request-forms", payload);

                        if (res.data && res.data.success) {
                            Swal.fire({
                                title: "Submitted!",
                                text: res.data.message || "Alternative device request has been successfully submitted.",
                                icon: "success",
                                customClass: { confirmButton: "btn btn-success w-xs" },
                                buttonsStyling: false,
                            }).then(() => {
                                this.$router.push("/device/alternative-devices");
                            });
                        } else {
                            throw new Error(res.data?.message || "Failed to submit request.");
                        }
                    } catch (error) {
                        console.error("Error submitting request:", error);
                        Swal.fire({
                            icon: "error",
                            title: "Submission Failed",
                            text: error.response?.data?.message || error.message || "Failed to submit alternative device request.",
                            customClass: { confirmButton: "btn btn-primary w-xs me-2" },
                            buttonsStyling: false,
                        });
                    } finally {
                        this.submitting = false;
                    }
                }
            });
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row">
            <div class="col-lg-12">

                <!-- Order Selection Card (Dropdown Selection) -->
                <div class="card mb-4">
                    <div class="card-header align-items-center d-flex bg-light-subtle">
                        <h5 class="card-title mb-0 flex-grow-1">
                            <i class="ri-search-line align-middle me-2 text-primary"></i> Select Order
                        </h5>
                        <span v-if="loadingOrders" class="spinner-border spinner-border-sm text-primary" role="status"></span>
                    </div>
                    <div class="card-body">
                        <div class="row align-items-center">
                            <div class="col-12 col-md-6">
                                <label class="form-label fw-semibold">
                                    Order ID <span class="text-danger">*</span>
                                </label>
                                <Multiselect
                                    v-model="selectedOrderObject"
                                    :options="fetchOrdersDropdown"
                                    :searchable="true"
                                    :can-deselect="false"
                                    :can-clear="true"
                                    :delay="300"
                                    :min-chars="0"
                                    :resolve-on-load="true"
                                    :object="true"
                                    placeholder="-- Type Order ID / Name / NRIC (e.g. ORD-2026-8821) --"
                                    @select="onOrderSelect"
                                >
                                    <template v-slot:noresults>
                                        <div class="multiselect-no-results py-2 px-3 text-muted text-start fs-13">
                                            <i class="ri-error-warning-line text-warning me-1 align-middle"></i>
                                            No eligible order found. Order ID may be invalid or not in <strong>Processing</strong> status.
                                        </div>
                                    </template>
                                    <template v-slot:nooptions>
                                        <div class="multiselect-no-options py-2 px-3 text-muted text-start fs-13">
                                            <i class="ri-information-line text-info me-1 align-middle"></i>
                                            No Processing orders available.
                                        </div>
                                    </template>
                                </Multiselect>
                                <div class="form-text text-muted mt-1">
                                    <i class="ri-information-line align-middle me-1 text-info"></i>
                                    Note: Only orders with <strong>Processing</strong> status are eligible for alternative device requests.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Loading State Spinner -->
                <div class="card card-body text-center py-5" v-if="loadingOrderDetails">
                    <div class="spinner-border text-primary me-2 mx-auto mb-2" role="status"></div>
                    <p class="text-muted mb-0">Loading order and device details...</p>
                </div>

                <!-- Placeholder state if no order is selected -->
                <div class="card card-body text-center py-5" v-if="!orderData && !loadingOrderDetails">
                    <i class="ri-file-search-line fs-48 text-muted mb-2"></i>
                    <h5 class="text-dark fw-bold mb-1">No Order Selected</h5>
                    <p class="text-muted mb-0">Please select an Order ID from the dropdown above to view order details and request an alternative device.</p>
                </div>

                <!-- Section 1: Order Details & Original Device Details (Shows once Order Selected) -->
                <div class="card" v-if="orderData && !loadingOrderDetails">
                    <div class="card-header bg-light-subtle flex-wrap flex-md-nowrap align-items-center d-flex gap-2">
                        <h5 class="card-title mb-0 flex-grow-1 w-100 w-md-auto">
                            <i class="ri-file-text-line align-middle me-2 text-primary"></i> Order &amp; Original Device Details
                        </h5>
                        <div class="d-flex align-items-center gap-2 w-100 w-md-auto justify-content-start justify-content-md-end mt-1 mt-md-0">
                            <span class="badge bg-primary fs-12 px-3 py-1">Order # {{ orderData.order ? orderData.order.orderId : selectedOrderId }}</span>
                            <router-link :to="'/orders/customer-order-details/' + (orderData.order && orderData.order.customer ? (orderData.order.customer.id || orderData.order.customer) : (orderData.order ? orderData.order.customer_id : ''))" target="_blank" class="btn btn-soft-primary btn-sm flex-shrink-0">
                                <i class="ri-external-link-line align-middle me-1"></i> View Order
                            </router-link>
                        </div>
                    </div>

                    <div class="card-body">
                        <div class="row g-4">
                            <!-- Order Info Column -->
                            <div class="col-lg-6 border-end-lg">
                                <h6 class="text-muted text-uppercase fw-semibold mb-3 fs-12">
                                    <i class="ri-user-line me-1"></i> Order Details
                                </h6>
                                <div class="table-responsive">
                                    <table class="table table-borderless table-sm mb-0">
                                        <tbody>
                                            <tr>
                                                <td class="fw-medium text-muted" style="width: 180px;">Order Date</td>
                                                <td class="fw-semibold text-dark">{{ orderData.order ? formatDate(orderData.order.created_at) : 'N/A' }}</td>
                                            </tr>
                                            <tr>
                                                <td class="fw-medium text-muted">Order Status</td>
                                                <td>
                                                    <span class="badge bg-primary-subtle text-primary border border-primary-subtle fs-12 px-2 py-1">
                                                        {{ orderData.order && (orderData.order.order_status?.name || orderData.order.order_status_name) ? (orderData.order.order_status?.name || orderData.order.order_status_name) : 'N/A' }}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="fw-medium text-muted">Customer Name</td>
                                                <td class="fw-semibold text-dark">{{ orderData.order && orderData.order.customer ? orderData.order.customer.name_as_per_nric : 'N/A' }}</td>
                                            </tr>
                                            <tr>
                                                <td class="fw-medium text-muted">Customer NRIC</td>
                                                <td class="fw-medium text-dark"><i class="ri-id-card-line me-1 text-muted"></i>{{ orderData.order && orderData.order.customer ? (orderData.order.customer.nric || 'N/A') : 'N/A' }}</td>
                                            </tr>
                                            <tr>
                                                <td class="fw-medium text-muted">Customer Contact</td>
                                                <td>
                                                    <div class="text-dark"><i class="ri-phone-line me-1 text-muted"></i>{{ orderData.order && orderData.order.customer ? (orderData.order.customer.phone_number || orderData.order.customer.phone || 'N/A') : 'N/A' }}</div>
                                                    <div class="text-muted fs-12" v-if="orderData.order && orderData.order.customer && orderData.order.customer.email">
                                                        <i class="ri-mail-line me-1 text-muted"></i>{{ orderData.order.customer.email }}
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="fw-medium text-muted">Tenure</td>
                                                <td class="fw-semibold text-dark">{{ orderData.order ? orderData.order.tenure + ' months' : 'N/A' }}</td>
                                            </tr>
                                            <tr v-if="orderData.order && (orderData.order.max_monthly_affordable !== null && orderData.order.max_monthly_affordable !== undefined)">
                                                <td class="fw-medium text-muted">Max Monthly Affordable</td>
                                                <td class="fw-bold text-success">{{ formatCurrency(orderData.order.max_monthly_affordable) }} / month</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <!-- Original Device Info Column -->
                            <div class="col-lg-6">
                                <h6 class="text-muted text-uppercase fw-semibold mb-3 fs-12">
                                    <i class="ri-smartphone-line me-1"></i> Original Device Details
                                </h6>
                                <div class="p-3 border rounded bg-light-subtle" v-if="orderData.current_device">
                                    <div class="d-flex align-items-center mb-3">
                                        <div class="avatar-sm me-3 flex-shrink-0">
                                            <div class="avatar-title bg-danger-subtle text-danger rounded fs-20">
                                                <i class="ri-cellphone-line"></i>
                                            </div>
                                        </div>
                                        <div>
                                            <h5 class="fs-15 mb-1 text-dark">{{ orderData.current_device.product ? orderData.current_device.product.product_name : orderData.current_device.sku }}</h5>
                                            <span class="badge bg-secondary-subtle text-secondary" v-if="getDeviceVariant(orderData.current_device)">
                                                {{ getDeviceVariant(orderData.current_device) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="row g-2 pt-2 border-top">
                                        <div class="col-4">
                                            <span class="text-muted fs-12 d-block">SKU</span>
                                            <span class="fw-semibold text-dark fs-13">{{ orderData.current_device.sku }}</span>
                                        </div>
                                        <div class="col-4">
                                            <span class="text-muted fs-12 d-block">Monthly Charges</span>
                                            <span class="fw-bold text-danger fs-13">{{ formatCurrency(orderData.order ? orderData.order.monthly_subscription : 0) }} / mo</span>
                                        </div>
                                        <div class="col-4">
                                            <span class="text-muted fs-12 d-block">Device Cost</span>
                                            <span class="fw-semibold text-dark fs-13">{{ formatCurrency(orderData.current_device.device_cost) }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="alert alert-warning mb-0" v-else>
                                    <i class="ri-alert-line me-1"></i> No current active device found for this order.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section 2: Request Alternative Device Form (Shows once Order Selected) -->
                <div class="card" v-if="orderData && !loadingOrderDetails">
                    <div class="card-header align-items-center d-flex bg-light-subtle">
                        <h5 class="card-title mb-0 flex-grow-1">
                            <i class="ri-swap-line align-middle me-2 text-success"></i> Request Alternative Device
                        </h5>
                    </div>

                    <div class="card-body">
                        <form @submit.prevent="submitRequest">
                            <div class="row g-4">
                                <!-- Request Type -->
                                <div class="col-lg-6">
                                    <label class="form-label fw-semibold">Request Type <span class="text-danger">*</span></label>
                                    <select class="form-select" v-model="requestType" @change="onRequestTypeChange" required>
                                        <option value="" disabled>Select Request Type</option>
                                        <option value="Color Change">Color Change</option>
                                        <option value="Inventory Unavailable">Inventory Unavailable</option>
                                        <option value="Customer Requested Change">Customer Requested Change</option>
                                    </select>
                                </div>

                                <!-- Alternative Devices Dropdown -->
                                <div class="col-lg-6">
                                    <label class="form-label fw-semibold d-flex align-items-center justify-content-between">
                                        <span>
                                            Alternative Device <span class="text-danger">*</span>
                                            <span v-if="loadingAlternatives" class="spinner-border spinner-border-sm text-primary ms-2" role="status"></span>
                                        </span>
                                        <span v-if="requestType && !loadingAlternatives" class="badge bg-info-subtle text-info border border-info-subtle font-normal">
                                            <i class="ri-smartphone-line me-1"></i>{{ availableAlternativeDevices.length }} {{ availableAlternativeDevices.length === 1 ? 'device available' : 'devices available' }}
                                        </span>
                                    </label>
                                    <Multiselect
                                        v-model="selectedDeviceId"
                                        :options="alternativeDeviceOptions"
                                        :searchable="true"
                                        :can-deselect="false"
                                        :can-clear="true"
                                        :disabled="loadingAlternatives || !requestType"
                                        :placeholder="!requestType ? 'Select Request Type first' : (availableAlternativeDevices.length ? 'Search & Select Alternative Device' : 'No alternative devices available')"
                                        @select="onDeviceSelect"
                                        @clear="onDeviceClear"
                                    />
                                </div>

                                <!-- Preview Selected Alternative Device Details -->
                                <div class="col-12" v-if="selectedAlternativeDevice">
                                    <div class="alert alert-success border-success mb-0" role="alert">
                                        <div class="d-flex align-items-center mb-2">
                                            <i class="ri-checkbox-circle-fill fs-20 me-2 text-success"></i>
                                            <h6 class="alert-heading mb-0 text-success fw-bold">Requested Alternative Device Preview</h6>
                                        </div>
                                        <div class="p-3 bg-white rounded border border-success-subtle">
                                            <div class="row align-items-center">
                                                <div class="col-md-4">
                                                    <span class="text-muted fs-12 d-block">Device Name &amp; Variants</span>
                                                    <h6 class="fw-bold text-dark mb-1">{{ selectedAlternativeDevice.product ? selectedAlternativeDevice.product.product_name : selectedAlternativeDevice.sku }}</h6>
                                                    <div class="d-flex align-items-center gap-1 flex-wrap mt-1">
                                                        <span class="badge bg-success-subtle text-success" v-if="getDeviceVariant(selectedAlternativeDevice)">
                                                            {{ getDeviceVariant(selectedAlternativeDevice) }}
                                                        </span>
                                                        <span class="badge bg-light text-muted border fs-11">
                                                            <i class="ri-barcode-line me-1"></i>{{ selectedAlternativeDevice.sku }}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div class="col-md-3">
                                                    <span class="text-muted fs-12 d-block">Monthly Charges</span>
                                                    <span :class="['fw-bold fs-14 d-block', getPriceDiff(selectedAlternativeDevice) ? getPriceDiff(selectedAlternativeDevice).textClass : 'text-dark']">
                                                        <i v-if="getPriceDiff(selectedAlternativeDevice)" :class="[getPriceDiff(selectedAlternativeDevice).icon, 'me-1']"></i>{{ formatCurrency(selectedAlternativeDevice.price) }} / mo
                                                    </span>
                                                    <small class="text-muted fs-11" v-if="orderData && orderData.order">
                                                        Current Device: {{ formatCurrency(orderData.order.monthly_subscription) }} / mo
                                                    </small>
                                                </div>
                                                <div class="col-md-3">
                                                    <span class="text-muted fs-12 d-block">Device Cost</span>
                                                    <span :class="['fw-bold fs-14 d-block', getCostDiff(selectedAlternativeDevice) ? getCostDiff(selectedAlternativeDevice).textClass : 'text-dark']">
                                                        <i v-if="getCostDiff(selectedAlternativeDevice)" :class="[getCostDiff(selectedAlternativeDevice).icon, 'me-1']"></i>{{ formatCurrency(selectedAlternativeDevice.device_cost) }}
                                                    </span>
                                                    <small class="text-muted fs-11" v-if="orderData && orderData.current_device">
                                                        Current Device: {{ formatCurrency(orderData.current_device.device_cost) }}
                                                    </small>
                                                </div>
                                                <div class="col-md-2 text-md-end">
                                                    <span class="text-muted fs-12 d-block">Tenure</span>
                                                    <span class="fw-bold text-primary fs-14">{{ selectedAlternativeDevice.tenure ? selectedAlternativeDevice.tenure + ' months' : 'N/A' }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Remark Textarea -->
                                <div class="col-12">
                                    <label class="form-label fw-semibold">Remark / Reason for Request <span class="text-danger">*</span></label>
                                    <textarea
                                        class="form-control"
                                        rows="4"
                                        v-model="remark"
                                        placeholder="Enter remarks or reasons for requesting an alternative device..."
                                        required
                                    ></textarea>
                                </div>

                                <!-- Submit & Cancel Buttons -->
                                <div class="col-12 text-end pt-2">
                                    <router-link to="/device/alternative-devices" class="btn btn-light me-2">
                                        Cancel
                                    </router-link>
                                    <button type="submit" class="btn btn-primary px-4" :disabled="submitting || !selectedDeviceId || !remark || !remark.trim() || !permissions.canRequestAlternativeDevices?.value">
                                        <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                        <i v-else class="ri-send-plane-fill align-middle me-1"></i> Submit Request
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

            </div>
        </div>
    </Layout>
</template>

<style scoped>
@media (min-width: 992px) {
    .border-end-lg {
        border-right: 1px dashed #e9ebec !important;
    }
}
</style>
